import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HiDownload } from 'react-icons/hi';
import { Modal, Button, TabContainer } from '@/components/common';
import { Customer } from '@/types/customer';
import { InvoiceResponse } from '@/types/invoice';
import { TransactionRecord } from '@/types/customer';

import { customerApi } from '@/api/endpoints/customers';
import { invoiceApi } from '@/api/endpoints/invoices';
import { receiptVoucherApi } from '@/api/endpoints/receiptVouchers';
import { toast } from '@/store/toastStore';
import CustomerInfoTab from './CustomerInfoTab';
import CustomerOrderHistory from '@/components/customers/CustomerOrderHistory';
import CustomerDebtTab from '@/components/customers/CustomerDebtTab';
import CustomerDebtExportModal from './CustomerDebtExportModal';
import { CustomerDebtNestedTableExport } from '@/utils/customerDebtNestedTableExport';

interface CustomerDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string | null; // Actually customer code, not ID
}

const CustomerDetailModal: React.FC<CustomerDetailModalProps> = ({
  isOpen,
  onClose,
  customerId,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'orders' | 'debt'>('debt');

  // Debt export modal state
  const [showDebtExportModal, setShowDebtExportModal] = useState(false);

  // Data for export (lazy loaded when needed)
  const [invoices] = useState<InvoiceResponse[]>([]);

  // Track date range from debt tab for export
  const [debtDateFrom, setDebtDateFrom] = useState('');
  const [debtDateTo, setDebtDateTo] = useState('');

  // Fetch customer detail when modal opens and reset state when modal closes
  useEffect(() => {
    if (isOpen && customerId) {
      fetchCustomerDetail();
    } else if (!isOpen) {
      // Reset state when modal closes
      setCustomer(null);
      setActiveTab('debt');
    }
  }, [isOpen, customerId]);

  const fetchCustomerDetail = async () => {
    if (!customerId) return;

    setLoading(true);
    try {
      const customerData = await customerApi.getCustomer(customerId);
      setCustomer(customerData);
    } catch (error) {
      console.error('Failed to fetch customer detail:', error);
      toast.error(t('common.error'), t('customers.messages.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCustomer(null);
    setActiveTab('debt'); // Reset to debt tab when closing
    onClose();
  };

  // Handle tab change
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as 'info' | 'orders' | 'debt');
  };

  // Excel export functionality for debt tab
  const handleExcelExport = async () => {
    if (!customer) {
      toast.error(t('common.error'), t('customers.messages.noCustomerSelected'));
      return;
    }

    // For debt tab, show date range selection modal
    if (activeTab === 'debt') {
      setShowDebtExportModal(true);
      return;
    }
  };

  // Debt export functionality
  const handleDebtExport = async (dateFrom: string, dateTo: string) => {
    if (!customer) {
      toast.error(t('common.error'), t('customers.messages.noCustomerSelected'));
      return;
    }

    try {
      // Fetch all transactions for the date range
      const totalRecords = 10000; // Large number to get all records
      const allDataResponse = await customerApi.getCustomerDebtReport({
        customerId: customer.code,
        dateFrom: dateFrom,
        dateTo: dateTo,
        page: 1,
        pageSize: totalRecords,
      });

      // Fetch summary for export
      const summaryForExport = await customerApi.getCustomerDebtSummary({
        customerId: customer.code,
        dateFrom: dateFrom,
        dateTo: dateTo,
      });

      // Fetch invoice details for all invoices
      const invoiceIds = allDataResponse.data.data
        .filter((tx: TransactionRecord) => tx.record_type === 'invoice')
        .map((tx: TransactionRecord) => tx.id);

      const invoiceDetailsMap = new Map();
      for (const invoiceId of invoiceIds) {
        try {
          const invoiceDetail = await invoiceApi.getInvoice(invoiceId);
          invoiceDetailsMap.set(invoiceId, invoiceDetail);
        } catch (error) {
          console.error(`Error fetching invoice ${invoiceId}:`, error);
        }
      }

      // Fetch receipt voucher details for all receipts
      const receiptIds = allDataResponse.data.data
        .filter((tx: TransactionRecord) => tx.record_type === 'receipt')
        .map((tx: TransactionRecord) => tx.code);

      const receiptDetailsMap = new Map();
      for (const receiptCode of receiptIds) {
        try {
          const receiptDetail = await receiptVoucherApi.getReceiptVoucher(receiptCode);
          receiptDetailsMap.set(receiptCode, receiptDetail);
        } catch (error) {
          console.error(`Error fetching receipt ${receiptCode}:`, error);
        }
      }

      // Validate data before export
      if (allDataResponse.data.data.length === 0) {
        toast.error(t('common.error'), t('customers.debtExport.noDataInRange') || 'No data in selected date range');
        return;
      }

      // Generate Excel export
      const debtExport = new CustomerDebtNestedTableExport({
        customer,
        transactions: allDataResponse.data.data,
        invoiceDetailsMap,
        receiptDetailsMap,
        dateFrom,
        dateTo,
        openingBalance: summaryForExport.opening_balance || 0,
        closingBalance: summaryForExport.closing_balance || 0,
        invoiceAmount: summaryForExport.invoice_amount || 0,
        receiptAmount: summaryForExport.receipt_amount || 0,
        t,
      });

      const excelBlob = await debtExport.generateExcel();

      if (excelBlob.size === 0) {
        toast.error(t('common.error'), t('customers.debtExport.emptyFileError') || 'Empty file error');
        return;
      }

      // Download the file
      const url = window.URL.createObjectURL(excelBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Cong_No_${customer.code}_${dateFrom}_${dateTo}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(t('common.success'), t('customers.debtExport.exportSuccess') || 'Export successful');
    } catch (error) {
      console.error('Debt export failed:', error);
      toast.error(t('common.error'), t('customers.debtExport.exportError') || 'Export failed');
    }
  };

  // Define tabs
  const tabs = [
    {
      id: 'info',
      label: t('customers.tabs.info'),
      content: (
        <CustomerInfoTab
          customer={customer}
          loading={loading}
        />
      ),
    },
    {
      id: 'orders',
      label: t('customers.tabs.orderHistory'),
      content: (
        <CustomerOrderHistory customerId={customer?.code || ''} />
      ),
    },
    {
      id: 'debt',
      label: t('customers.tabs.debt'),
      content: (
        <CustomerDebtTab
          customer={customer}
          onDateRangeChange={(from, to) => {
            setDebtDateFrom(from);
            setDebtDateTo(to);
          }}
        />
      ),
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('customers.detail')}
      size="2xl"
      footer={
        <div className="flex items-center justify-between w-full">
          {/* Excel export button on the left (show for debt tab) */}
          {activeTab === 'debt' && (
            <Button
              variant="outline"
              size="md"
              onClick={handleExcelExport}
              disabled={loading || !customer}
              icon={<HiDownload className="h-4 w-4" />}
            >
              {t('warehouseReceipts.quickView.exportExcel')}
            </Button>
          )}

          {/* Close button on the right */}
          <div className={activeTab === 'debt' ? '' : 'ml-auto'}>
            <Button
              variant="primary"
              size="md"
              onClick={handleClose}
            >
              {t('buttons.close')}
            </Button>
          </div>
        </div>
      }
    >
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">{t('common.loading')}</div>
        </div>
      )}

      {!loading && !customer && (
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">{t('customers.messages.noCustomerSelected')}</div>
        </div>
      )}

      {!loading && customer && (
        <TabContainer
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          className="h-full"
        />
      )}

      {/* Debt Export Modal */}
      <CustomerDebtExportModal
        isOpen={showDebtExportModal}
        onClose={() => setShowDebtExportModal(false)}
        customer={customer}
        invoices={invoices}
        onExport={handleDebtExport}
        initialDateFrom={debtDateFrom}
        initialDateTo={debtDateTo}
      />
    </Modal>
  );
};

export default CustomerDetailModal;
